import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Plus, Trash2, Receipt, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { apiService } from '@/lib/api';

// Common pre-project expense templates - pre-fill rows so users can just enter amounts
const COMMON_PREPAID_EXPENSES = [
  { description: 'Building Permit', vendor: '' },
  { description: 'Architect / Design Fees', vendor: '' },
  { description: 'Soil Test / Geotech Report', vendor: '' },
  { description: 'Land Survey', vendor: '' },
  { description: 'Plan Review Fee', vendor: '' },
  { description: 'Impact / Utility Connection Fee', vendor: '' },
];

const emptyRow = () => ({
  description: '',
  vendor: '',
  amount: '',
  date_paid: '',
  notes: '',
});

/**
 * Bulk import pre-project / pre-paid soft cost expenses.
 * Each row is created as a soft_cost phase with is_prepaid=true so it bypasses
 * lien waiver, W9, COI, inspection and completion-photo requirements.
 */
export const PrePaidExpensesDialog = ({ open, onOpenChange, projectId, onComplete }) => {
  const [rows, setRows] = useState(() =>
    COMMON_PREPAID_EXPENSES.map((p) => ({ ...emptyRow(), description: p.description, vendor: p.vendor })),
  );
  const [saving, setSaving] = useState(false);

  const updateRow = (idx, field, value) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  const addRow = () => setRows((prev) => [...prev, emptyRow()]);
  const removeRow = (idx) => setRows((prev) => prev.filter((_, i) => i !== idx));

  const totalAmount = rows.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
  const validRowCount = rows.filter((r) => r.description?.trim() && parseFloat(r.amount) > 0).length;

  const handleSubmit = async () => {
    const items = rows
      .filter((r) => r.description?.trim() && parseFloat(r.amount) > 0)
      .map((r) => ({
        description: r.description.trim(),
        vendor: r.vendor?.trim() || null,
        amount: parseFloat(r.amount),
        date_paid: r.date_paid || null,
        notes: r.notes?.trim() || null,
      }));

    if (items.length === 0) {
      toast.error('Add at least one expense with a description and amount');
      return;
    }

    setSaving(true);
    try {
      const res = await apiService.bulkCreatePrePaidExpenses(projectId, items, 1);
      toast.success(res.data?.message || `Added ${items.length} pre-paid expenses`);
      if (onComplete) onComplete(res.data);
      onOpenChange(false);
      // Reset to defaults for next open
      setRows(COMMON_PREPAID_EXPENSES.map((p) => ({ ...emptyRow(), description: p.description, vendor: p.vendor })));
    } catch (err) {
      console.error('Bulk import failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to save pre-paid expenses');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="prepaid-expenses-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Receipt className="w-5 h-5 text-amber-600" />
            Pre-Project Expenses
          </DialogTitle>
          <DialogDescription>
            Add expenses you've already paid before starting construction (permits, architect fees, soil tests, etc.).
            These are auto-marked as paid and bypass lien waiver, W-9, and inspection requirements.
          </DialogDescription>
        </DialogHeader>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 flex items-start gap-2">
          <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <p>
            Quick-fill rows below — leave blank or remove rows you don't need. Categorized under <strong>Phase 1: Planning &amp; Permitting</strong>.
          </p>
        </div>

        <div className="space-y-2">
          {/* Header row */}
          <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-slate-600 px-1">
            <div className="col-span-4">Description</div>
            <div className="col-span-3">Vendor</div>
            <div className="col-span-2">Amount</div>
            <div className="col-span-2">Date Paid</div>
            <div className="col-span-1"></div>
          </div>

          {rows.map((row, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2" data-testid={`prepaid-row-${idx}`}>
              <Input
                className="col-span-4"
                placeholder="e.g., Building Permit"
                value={row.description}
                onChange={(e) => updateRow(idx, 'description', e.target.value)}
                data-testid={`prepaid-description-${idx}`}
              />
              <Input
                className="col-span-3"
                placeholder="e.g., City of Cedar Rapids"
                value={row.vendor}
                onChange={(e) => updateRow(idx, 'vendor', e.target.value)}
                data-testid={`prepaid-vendor-${idx}`}
              />
              <Input
                className="col-span-2"
                type="number"
                placeholder="0.00"
                value={row.amount}
                onChange={(e) => updateRow(idx, 'amount', e.target.value)}
                data-testid={`prepaid-amount-${idx}`}
              />
              <Input
                className="col-span-2"
                type="date"
                value={row.date_paid}
                onChange={(e) => updateRow(idx, 'date_paid', e.target.value)}
                data-testid={`prepaid-date-${idx}`}
              />
              <Button
                variant="ghost"
                size="sm"
                className="col-span-1 text-red-500 hover:text-red-700"
                onClick={() => removeRow(idx)}
                disabled={rows.length === 1}
                data-testid={`prepaid-remove-${idx}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}

          <Button variant="outline" size="sm" onClick={addRow} className="mt-2" data-testid="prepaid-add-row">
            <Plus className="w-4 h-4 mr-1" /> Add Row
          </Button>
        </div>

        {/* Summary */}
        <div className="border-t pt-3 flex items-center justify-between bg-slate-50 -mx-6 px-6 -mb-2 py-3">
          <div className="text-sm text-slate-600">
            <span className="font-semibold text-slate-900" data-testid="prepaid-valid-count">{validRowCount}</span> expense{validRowCount !== 1 ? 's' : ''} ready to import
          </div>
          <div className="text-lg font-bold text-amber-700" data-testid="prepaid-total">
            Total: ${totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="prepaid-cancel">
            Skip / Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={saving || validRowCount === 0}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="prepaid-save"
          >
            {saving ? 'Saving...' : `Import ${validRowCount} Expense${validRowCount !== 1 ? 's' : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PrePaidExpensesDialog;
